import React from "react";
import SectionHeading from "@/components/ui/SectionHeading";
import useScrollReveal from "@/hooks/useScrollReveal";
import { COLORS, FONTS, LAYOUT } from "@/constants/theme";

/**
 * El método de ALTAMA tal como lo fija el one-pager institucional:
 * "De una pregunta de negocio a una decisión accionable".
 */
const ETAPAS = [
  {
    numero: "01",
    titulo: "Datos",
    descripcion: "Reunimos y depuramos las fuentes internas y externas que hacen falta para responder la pregunta.",
  },
  {
    numero: "02",
    titulo: "Análisis",
    descripcion: "Exploramos, validamos y contrastamos lo que los datos dicen y lo que no pueden decir todavía.",
  },
  {
    numero: "03",
    titulo: "Modelos",
    descripcion: "Aplicamos estadística y econometría para medir relaciones, estimar efectos y proyectar escenarios.",
  },
  {
    numero: "04",
    titulo: "Hallazgos",
    descripcion: "Traducimos los resultados a evidencia interpretable, con sus supuestos y sus límites explícitos.",
  },
  {
    numero: "05",
    titulo: "Decisión",
    descripcion: "Entregamos una recomendación accionable y los indicadores para seguirla en el tiempo.",
  },
];

/**
 * Sección de método: el recorrido de una pregunta de negocio hasta la decisión.
 *
 * @returns {JSX.Element}
 */
const PropuestaValorSection = () => {
  const [headingRef, headingVisible] = useScrollReveal(0.2);
  const [pasosRef, pasosVisible] = useScrollReveal(0.12);

  return (
    <section
      className="px-6 py-24 md:py-32"
      id="propuesta-valor"
      style={{
        backgroundColor: COLORS.lightSurface,
        borderTop: `1px solid ${COLORS.lightBorder}`,
        scrollMarginTop: LAYOUT.scrollOffset,
      }}
    >
      <div className="mx-auto" style={{ maxWidth: LAYOUT.maxWidth }}>
        <div className={`reveal ${headingVisible ? "visible" : ""}`} ref={headingRef}>
          <SectionHeading
            align="left"
            lineColor={COLORS.digitalBlue}
            subtitle="De una pregunta de negocio a una decisión accionable. Cada etapa deja algo entregable, revisable y trazable."
            subtitleColor={COLORS.lightMuted}
            title="Cómo trabajamos"
            titleColor={COLORS.lightText}
          />
        </div>

        <ol
          className={`etapas reveal ${pasosVisible ? "visible" : ""} relative mt-14 grid grid-cols-1 gap-x-6 gap-y-10 sm:grid-cols-2 lg:grid-cols-5`}
          ref={pasosRef}
          style={{ listStyle: "none", margin: "3.5rem 0 0", padding: 0 }}
        >
          {ETAPAS.map((etapa, index) => (
            <li className="etapa" key={etapa.numero} style={{ position: "relative" }}>
              {/* Raíl hacia la etapa siguiente. La última no lo lleva: no hay nada que enlazar. */}
              {index < ETAPAS.length - 1 ? (
                <span
                  aria-hidden="true"
                  className="absolute hidden lg:block"
                  style={{
                    top: "17px",
                    left: "0",
                    right: "-1.5rem",
                    height: "1px",
                    backgroundColor: COLORS.lightBorder,
                  }}
                />
              ) : null}

              <span
                aria-hidden="true"
                className="etapa-numero relative inline-flex items-center justify-center"
                style={{
                  width: "34px",
                  height: "34px",
                  borderRadius: "50%",
                  backgroundColor: index === ETAPAS.length - 1 ? COLORS.digitalBlue : COLORS.lightSurface,
                  border: `1.5px solid ${index === ETAPAS.length - 1 ? COLORS.digitalBlue : COLORS.lightBorder}`,
                  color: index === ETAPAS.length - 1 ? COLORS.lightSurface : COLORS.digitalBlue,
                  fontFamily: FONTS.data,
                  fontSize: "0.74rem",
                  letterSpacing: "0.04em",
                }}
              >
                {etapa.numero}
              </span>

              <h3
                className="mt-4 text-[1.16rem]"
                style={{
                  color: COLORS.lightText,
                  fontFamily: FONTS.display,
                  fontWeight: 600,
                  lineHeight: 1.3,
                }}
              >
                {etapa.titulo}
              </h3>

              <p
                className="mt-2 text-[0.95rem]"
                style={{
                  color: COLORS.lightMuted,
                  fontFamily: FONTS.body,
                  lineHeight: 1.65,
                }}
              >
                {etapa.descripcion}
              </p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
};

export default PropuestaValorSection;
